import * as i0 from '@angular/core';
import { Component, ViewChild, HostListener, Directive, EventEmitter, Input, Output, QueryList, ContentChildren, ContentChild, NgModule, TemplateRef, ViewEncapsulation } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { Subject, defer, merge } from 'rxjs';
import { takeUntil, take, switchMap } from 'rxjs/operators';
import * as i2 from '@angular/cdk/portal';
import { TemplatePortal, PortalModule } from '@angular/cdk/portal';
import { TabbableOption, RovingTabindex } from '@anthem/uxd/util';

/**
 *  Sub nav Component. Holds left/right mask booleans and references to elements in view
 */
class SubNavComponent {
    /**
     *  Inject renderer service
     */
    constructor(renderer) {
        this.renderer = renderer;
        this._isSubnavLoaded = false;
    }
    /**
     *  After content/view loads, apply focus to navbar for keyboard users
     *  and listen to scroll position. Apply left/right mask based on scroll pos;
     *  If sub nav is not overflowing do not show right mask
     */
    ngAfterViewInit() {
        this.navElRef.nativeElement.focus();
        this.attachScrollListener();
    }
    /**
     * Waits until transcluded child content has been detected to run calcOverflow() method
     * Flip flag that it has been run so it doesn't run every time
     */
    ngAfterViewChecked() {
        if (this.listCtnrElRef.nativeElement.childElementCount > 0 && !this._isSubnavLoaded) {
            this.calcOverflow();
            this._isSubnavLoaded = !this._isSubnavLoaded;
        }
    }
    /**
     *  On window resize, re-adjust & check if still overflowing or if width
     *  is large enough to fit sub nav list items
     */
    onResize(event) {
        this.calcOverflow();
    }
    /**
     * Attach scroll masks based on the width of the nav bar && attach scroll listener
     */
    attachScrollListener() {
        this.calcOverflow();
        this.renderer.listen(this.listCtnrElRef.nativeElement, 'scroll', (scrollEvt) => {
            if (this.listCtnrElRef && this.listCtnrElRef.nativeElement) {
                // get scrollbar position
                this._scrollbarPos = Math.floor(this.listCtnrElRef.nativeElement.scrollLeft);
                // if scrollbar position is at max scroll distance, disable right mask
                if (this._scrollbarPos === this._maxScrollbarDist || this._scrollbarPos + 1 === this._maxScrollbarDist) {
                    this._rightMask = false;
                }
                else {
                    this._rightMask = true;
                }
                // if scrollbar position is at 0, disable left mask
                this._leftMask = this._scrollbarPos === 0 ? false : true;
            }
        });
    }
    calcOverflow() {
        setTimeout(() => {
            let isOverflowing = this.isOverflowing();
            this.calcMaxScrollPos();
            if (isOverflowing) {
                this.renderer.removeClass(this.listCtnrElRef.nativeElement, 'no-overflow');
            }
            else {
                this.renderer.addClass(this.listCtnrElRef.nativeElement, 'no-overflow');
            }
            this._rightMask = isOverflowing;
        }, 250);
    }
    /**
     * Helper function to determine if list element container is overflowing
     * Screen width is less than total width (shown off screen)
     */
    isOverflowing() {
        if (this.listCtnrElRef.nativeElement && this.listCtnrElRef.nativeElement.clientWidth < this.listCtnrElRef.nativeElement.scrollWidth) {
            return true;
        }
        return false;
    }
    /**
     *  Calculate the maximum total width of sub nav component
     */
    calcMaxScrollPos() {
        if (this.listCtnrElRef && this.listCtnrElRef.nativeElement && this.isOverflowing()) {
            this._maxScrollbarDist = Math.floor(this.listCtnrElRef.nativeElement.scrollWidth) - Math.floor(this.listCtnrElRef.nativeElement.clientWidth);
        }
        else {
            this._maxScrollbarDist = 0;
        }
    }
    get leftMask() {
        return this._leftMask;
    }
    get rightMask() {
        return this._rightMask;
    }
    get maxScrollPos() {
        return this._maxScrollbarDist;
    }
    get scrollPos() {
        return this._scrollbarPos;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SubNavComponent, deps: [{ token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SubNavComponent, selector: "[data-uxd-sub-nav-cmp]", host: { listeners: { "window:resize": "onResize($event)" } }, viewQueries: [{ propertyName: "navElRef", first: true, predicate: ["nav"], descendants: true }, { propertyName: "listCtnrElRef", first: true, predicate: ["list"], descendants: true }], ngImport: i0, template: `
  <nav class="ant-sub-navigation-container" tabindex="-1" #nav id="ant-sub-nav-container">
    <div class="ant-fringe-mask-left" *ngIf="leftMask"></div>
    <div class="ant-fringe-mask" *ngIf="rightMask"></div>
    <div class="ant-sub-navigation body-sm" #list id="ant-sub-nav-list">
      <ng-content></ng-content>
    </div>
  </nav>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SubNavComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-sub-nav-cmp]',
                    template: `
  <nav class="ant-sub-navigation-container" tabindex="-1" #nav id="ant-sub-nav-container">
    <div class="ant-fringe-mask-left" *ngIf="leftMask"></div>
    <div class="ant-fringe-mask" *ngIf="rightMask"></div>
    <div class="ant-sub-navigation body-sm" #list id="ant-sub-nav-list">
      <ng-content></ng-content>
    </div>
  </nav>
  `
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }]; }, propDecorators: { navElRef: [{
                type: ViewChild,
                args: ['nav']
            }], listCtnrElRef: [{
                type: ViewChild,
                args: ['list']
            }], onResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });

/**
 * Siderail Navigation Event
 */
class SideRailNavEvent {
    constructor(link, nav) {
        this.link = link;
        this.nav = nav;
    }
}
/**
 * Siderail Header
 */
class SideRailHeaderDirective {
    constructor(_host) {
        this._host = _host;
    }
    ngAfterViewInit() {
        if (this._host && this._host.nativeElement) {
            this.id = this._host.nativeElement.id;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SideRailHeaderDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: SideRailHeaderDirective, selector: "[siderail-header]", host: { classAttribute: "siderail-header" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SideRailHeaderDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: "[siderail-header]",
                    host: {
                        class: "siderail-header"
                    }
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });
/**
 * Siderail Link
 */
class SideRailLinkComponent {
    get isActive() {
        return this._isActive;
    }
    set isActive(isActive) {
        if (this._isActive !== isActive) {
            this._isActive = isActive;
        }
    }
    constructor(_element, _changeDetectorRef) {
        this._element = _element;
        this._changeDetectorRef = _changeDetectorRef;
        this._destroyed = new Subject();
        this._isActive = false;
        this.onActive = new EventEmitter();
        if (this._parent && this._changeDetectorRef) {
            this._parent._stateChanges
                .pipe(takeUntil(this._destroyed))
                .subscribe(() => {
                this._changeDetectorRef.markForCheck();
            });
        }
    }
    ngOnDestroy() {
        this._destroyed.next();
        this._destroyed.complete();
    }
    handleClick() {
        this.activate(true);
    }
    /**
     * Activate the link, and update its style to be 'active'
     * @param isViaUser Whether the activation was programatic, or the result
     * of user interaction
     */
    activate(isViaUser) {
        this.isActive = true;
        this._emitActiveState(isViaUser);
    }
    /**
     * Deactivate the link, and remove any 'active' styling
     * @param isViaUser Whether the activation was programatic, or the result
     * of user interaction
     */
    deactivate(isViaUser) {
        this.isActive = false;
        this._emitActiveState(isViaUser);
    }
    /**
     * Toggle active state
     * @param isViaUser Whether the activation was programatic, or the result
     * of user interaction
     */
    toggle(isViaUser) {
        this.isActive = !this.isActive;
        this._emitActiveState(isViaUser);
    }
    /** Retrieves the DOM element of the component host. */
    _getHostElement() {
        return this._element.nativeElement;
    }
    _emitActiveState(isUserInteraction = false) {
        if (isUserInteraction) {
            this.onActive.next(this);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SideRailLinkComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SideRailLinkComponent, selector: "a[siderail-link], button[siderail-link]", inputs: { isActive: ["is-active", "isActive"] }, outputs: { onActive: "onActive" }, host: { listeners: { "click": "handleClick()" }, properties: { "attr.data-is-active": "isActive", "class.is-active": "isActive" }, classAttribute: "sidebar-item" }, exportAs: ["siderail-link"], ngImport: i0, template: `
      <ng-content></ng-content>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SideRailLinkComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "a[siderail-link], button[siderail-link]",
                    exportAs: "siderail-link",
                    template: `
      <ng-content></ng-content>
  `,
                    host: {
                        class: "sidebar-item",
                        "[attr.data-is-active]": "isActive",
                        "[class.is-active]": "isActive"
                    }
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { isActive: [{
                type: Input,
                args: ["is-active"]
            }], onActive: [{
                type: Output
            }], handleClick: [{
                type: HostListener,
                args: ["click"]
            }] } });
/**
 * Sideraile Navigation
 */
class SideRailNavComponent {
    get isActive() {
        return this._isActive;
    }
    get anchorLinks() {
        return this._anchorLinks.toArray();
    }
    constructor(_ngZone) {
        this._ngZone = _ngZone;
        this._stateChanges = new Subject();
        this.onActive = new EventEmitter();
        this._isActive = false;
        this._anchorLinks = new QueryList();
        this._activeLinkChanges = (defer(() => {
            if (this._anchorLinks) {
                return merge(...this._anchorLinks.map(item => item.onActive));
            }
            return this._ngZone.onStable.asObservable().pipe(take(1), switchMap(() => this._activeLinkChanges));
        }));
    }
    ngAfterViewInit() {
        this._activeLinkChanges.subscribe((activeItem) => {
            this._isActive = true;
            //on change reset list items to fals
            this.resetAll();
            //change click item active status to true
            this.onActive.emit(new SideRailNavEvent(activeItem, this));
        });
    }
    ngOnChanges() {
        this._stateChanges.next();
    }
    ngOnDestroy() {
        this._stateChanges.complete();
    }
    _ariaLabelledBy() {
        return this._navHeader.id || null;
    }
    _activate() {
        this._isActive = true;
    }
    _deactivate() {
        this._isActive = false;
        this.resetAll();
    }
    resetAll() {
        this._anchorLinks.forEach(link => link.deactivate(false));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SideRailNavComponent, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SideRailNavComponent, selector: "siderail-nav", outputs: { onActive: "onActive" }, queries: [{ propertyName: "_navHeader", first: true, predicate: SideRailHeaderDirective, descendants: true }, { propertyName: "_anchorLinks", predicate: SideRailLinkComponent }], usesOnChanges: true, ngImport: i0, template: "<div class=\"sidebar-nav\" [attr.data-is-active]=\"isActive\">\n  <ng-content select=\"[siderail-header]\"></ng-content>\n  <nav\n    class=\"sidebar-list\"\n    [attr.aria-labelledby]=\"_ariaLabelledBy()\" >\n    <ng-content select=\"[siderail-link]\"></ng-content>\n  </nav>\n</div>" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SideRailNavComponent, decorators: [{
            type: Component,
            args: [{ selector: "siderail-nav", template: "<div class=\"sidebar-nav\" [attr.data-is-active]=\"isActive\">\n  <ng-content select=\"[siderail-header]\"></ng-content>\n  <nav\n    class=\"sidebar-list\"\n    [attr.aria-labelledby]=\"_ariaLabelledBy()\" >\n    <ng-content select=\"[siderail-link]\"></ng-content>\n  </nav>\n</div>" }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }]; }, propDecorators: { onActive: [{
                type: Output
            }], _anchorLinks: [{
                type: ContentChildren,
                args: [SideRailLinkComponent]
            }], _navHeader: [{
                type: ContentChild,
                args: [SideRailHeaderDirective]
            }] } });
/**
 * Sidebar Rail
 */
class SidebarRailComponent {
    constructor(_ngZone) {
        this._ngZone = _ngZone;
        //build list of all link items in case an item under another header is clicked
        this._navs = new QueryList();
        this._activeLinkChanges = defer(() => {
            if (this._navs) {
                return merge(...this._navs.map(item => item.onActive));
            }
            return this._ngZone.onStable.asObservable().pipe(take(1), switchMap(() => this._activeLinkChanges));
        });
    }
    ngAfterContentInit() {
        this._activeLinkChanges.subscribe((event) => {
            this._navs.forEach(nav => nav._deactivate());
            event.link.activate(false);
            event.nav._activate();
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidebarRailComponent, deps: [{ token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SidebarRailComponent, selector: "sidebar-rail", queries: [{ propertyName: "_navs", predicate: SideRailNavComponent }], ngImport: i0, template: `<aside class="sidebar-rail"><ng-content></ng-content></aside>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidebarRailComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "sidebar-rail",
                    template: `<aside class="sidebar-rail"><ng-content></ng-content></aside>`
                }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }]; }, propDecorators: { _navs: [{
                type: ContentChildren,
                args: [SideRailNavComponent]
            }] } });

class SidebarRailModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidebarRailModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SidebarRailModule, declarations: [SidebarRailComponent, SideRailNavComponent, SideRailHeaderDirective, SideRailLinkComponent], imports: [CommonModule], exports: [SidebarRailComponent, SideRailNavComponent, SideRailHeaderDirective, SideRailLinkComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidebarRailModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidebarRailModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [SidebarRailComponent, SideRailNavComponent, SideRailHeaderDirective, SideRailLinkComponent],
                    exports: [SidebarRailComponent, SideRailNavComponent, SideRailHeaderDirective, SideRailLinkComponent]
                }]
        }] });

let uniqueId$1 = 0;
/**
 * SidenavTabComponent
 * Button component that handles active state and accessibility of a tab.
 * https://www.w3.org/TR/2019/NOTE-wai-aria-practices-1.1-20190207/examples/tabs/tabs-1/tabs.html
 */
class SidenavTabComponent extends TabbableOption {
    get isActive() {
        return this._isActive;
    }
    set isActive(isActive) {
        if (this._isActive !== isActive) {
            this._isActive = isActive;
        }
    }
    get id() {
        return this._uniqueID;
    }
    set id(id) {
        this._uniqueID = id;
    }
    // tslint:disable-next-line: no-empty
    constructor(_renderer, _el) {
        super(_el);
        this._renderer = _renderer;
        this.change = new EventEmitter();
        this.active = new EventEmitter();
        this._isActive = false;
        this._uniqueID = `uxd-sidenav-tab-${uniqueId$1++}`;
    }
    // tslint:disable-next-line: no-empty
    ngOnInit() { }
    onClick() {
        this._emitOnChange();
    }
    _emitOnChange() {
        this.change.emit(this);
        this.active.emit();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavTabComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SidenavTabComponent, selector: "button[uxd-sidenav-tab]", inputs: { activeClass: "activeClass", isActive: ["is-active", "isActive"], id: "id" }, outputs: { change: "change", active: "active" }, host: { attributes: { "role": "tab" }, listeners: { "click": "onClick()" }, properties: { "class.is-active": "isActive", "attr.aria-selected": "isActive", "id": "id", "attr.aria-controls": "_panelId" }, classAttribute: "uxd-sidenav-tab" }, usesInheritance: true, ngImport: i0, template: `
  <div class="uxd-sidenav-tab-body">
    <ng-content></ng-content>
  </div>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavTabComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'button[uxd-sidenav-tab]',
                    template: `
  <div class="uxd-sidenav-tab-body">
    <ng-content></ng-content>
  </div>`,
                    host: {
                        class: 'uxd-sidenav-tab',
                        '(click)': 'onClick()',
                        '[class.is-active]': 'isActive',
                        role: 'tab',
                        '[attr.aria-selected]': 'isActive',
                        '[id]': 'id',
                        '[attr.aria-controls]': '_panelId'
                    }
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { change: [{
                type: Output
            }], active: [{
                type: Output
            }], activeClass: [{
                type: Input
            }], isActive: [{
                type: Input,
                args: ['is-active']
            }], id: [{
                type: Input
            }] } });
/**
 * SidenavLinkComponent
 * Link directive that relies on router outlet to work.
 * Should be used exclusively with the SidenavComponent.
 */
class SidenavLinkComponent {
    get isActive() {
        return this._isActive;
    }
    set isActive(isActive) {
        if (this._isActive !== isActive) {
            this._isActive = isActive;
        }
    }
    constructor(_element) {
        this._element = _element;
        this.isSelected = new EventEmitter();
        this._isActive = false;
    }
    // tslint:disable-next-line: no-empty
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavLinkComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: SidenavLinkComponent, selector: "a[uxd-sidenav-link]", inputs: { isActive: ["active", "isActive"] }, outputs: { isSelected: "isSelected" }, host: { properties: { "class.is-active": "isActive" }, classAttribute: "uxd-sidenav-link" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavLinkComponent, decorators: [{
            type: Directive,
            args: [{
                    selector: 'a[uxd-sidenav-link]',
                    host: {
                        class: 'uxd-sidenav-link',
                        '[class.is-active]': 'isActive'
                    }
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { isSelected: [{
                type: Output
            }], isActive: [{
                type: Input,
                args: ['active']
            }] } });
class SidenavNavComponent {
    // tslint:disable-next-line: no-empty
    constructor() { }
    // tslint:disable-next-line: no-empty
    ngOnInit() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavNavComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SidenavNavComponent, selector: "uxd-sidenav-nav", inputs: { selected: "selected" }, ngImport: i0, template: "<div class=\"uxd-sidebar-wrapper\">\n  <nav>\n    <ng-content></ng-content>\n  </nav>\n</div>" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavNavComponent, decorators: [{
            type: Component,
            args: [{ selector: 'uxd-sidenav-nav', template: "<div class=\"uxd-sidebar-wrapper\">\n  <nav>\n    <ng-content></ng-content>\n  </nav>\n</div>" }]
        }], ctorParameters: function () { return []; }, propDecorators: { selected: [{
                type: Input
            }] } });

let uniqueId = 0;
class TabPanelContentDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabPanelContentDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: TabPanelContentDirective, selector: "[uxd-panel-content]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabPanelContentDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[uxd-panel-content]'
                }]
        }] });
/**
 * SidenavBodyPanelComponent
 * Panel component that handles active state and accessibility of a panel.
 * https://www.w3.org/TR/2019/NOTE-wai-aria-practices-1.1-20190207/examples/tabs/tabs-1/tabs.html
 */
class SidenavBodyPanelComponent {
    get content() {
        return this._contentPortal;
    }
    show() {
        this._isVisible = true;
    }
    hide() {
        this._isVisible = false;
    }
    set id(id) {
        this._id = id;
    }
    get id() {
        return this._id;
    }
    get isVisible() {
        return this._isVisible;
    }
    constructor(_host, _viewContainerRef) {
        this._host = _host;
        this._viewContainerRef = _viewContainerRef;
        this._contentPortal = null;
        this._id = `uxd-sidenav-panel-${uniqueId++}`;
    }
    // tslint:disable-next-line: no-empty
    ngOnInit() { }
    ngAfterContentInit() {
        if (this._explicitContent) {
            this._contentPortal = new TemplatePortal(this._explicitContent, this._viewContainerRef);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavBodyPanelComponent, deps: [{ token: i0.ElementRef }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SidenavBodyPanelComponent, selector: "uxd-sidenav-panel", inputs: { id: "id" }, host: { attributes: { "role": "tabpanel" }, properties: { "id": "id" }, classAttribute: "sidenav-panel" }, queries: [{ propertyName: "_explicitContent", first: true, predicate: TabPanelContentDirective, descendants: true, read: TemplateRef }], ngImport: i0, template: `
  <ng-container *ngIf="isVisible">
    <ng-template [ngIf]="content">
      <ng-template [cdkPortalOutlet]="content"></ng-template>
    </ng-template>
    <ng-template [ngIf]="!content">
      <ng-content></ng-content>
    </ng-template>
  </ng-container>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavBodyPanelComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'uxd-sidenav-panel',
                    template: `
  <ng-container *ngIf="isVisible">
    <ng-template [ngIf]="content">
      <ng-template [cdkPortalOutlet]="content"></ng-template>
    </ng-template>
    <ng-template [ngIf]="!content">
      <ng-content></ng-content>
    </ng-template>
  </ng-container>
  `,
                    host: {
                        role: 'tabpanel',
                        '[id]': 'id',
                        class: 'sidenav-panel'
                    }
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ViewContainerRef }]; }, propDecorators: { _explicitContent: [{
                type: ContentChild,
                args: [TabPanelContentDirective, { read: TemplateRef }]
            }], id: [{
                type: Input
            }] } });
/**
 * SidenavBodyComponent
 * Body component that handles state of the panels the body accessibility tags(aria, tabindex).
 * https://www.w3.org/TR/2019/NOTE-wai-aria-practices-1.1-20190207/examples/tabs/tabs-1/tabs.html
 */
class SidenavBodyComponent {
    get hasPanels() {
        return this._panels.length > 0;
    }
    get panels() {
        return this._panels.toArray();
    }
    setSelected(selected) {
        const panels = this._panels.toArray();
        const selectedTab = panels[selected];
        const bodyEl = this.hostEl.nativeElement;
        this._resetPanels();
        this._renderer.setAttribute(bodyEl, 'data-selected-index', `${selected}`);
        if (selectedTab) {
            selectedTab.show();
        }
    }
    get tabLabelIds() {
        return this._tabLabelIds;
    }
    set tabLabelIds(tabIds) {
        this._tabLabelIds = tabIds;
        this._setAriaLabelledBy(this.tabLabelIds);
    }
    constructor(_renderer) {
        this._renderer = _renderer;
        this._tabLabelIds = [];
    }
    // tslint:disable-next-line: no-empty
    ngOnInit() { }
    _resetPanels() {
        const panels = this._panels.toArray();
        panels.forEach((panel) => {
            panel.hide();
        });
    }
    _setAriaLabelledBy(labelIds) {
        if (!labelIds || !labelIds.length) {
            return;
        }
        const panels = this._panels.toArray();
        panels.forEach((panel, idx) => {
            const panelEl = panel._host.nativeElement;
            this._renderer.setAttribute(panelEl, 'aria-labelledby', labelIds[idx]);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavBodyComponent, deps: [{ token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SidenavBodyComponent, selector: "uxd-sidenav-body", host: { classAttribute: "uxd-sidenav-body" }, queries: [{ propertyName: "_panels", predicate: SidenavBodyPanelComponent }], viewQueries: [{ propertyName: "hostEl", first: true, predicate: ["host"], descendants: true }], ngImport: i0, template: `
  <div #host class="sidenav-body-wrapper">
    <ng-content></ng-content>
  </div>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavBodyComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'uxd-sidenav-body',
                    template: `
  <div #host class="sidenav-body-wrapper">
    <ng-content></ng-content>
  </div>`,
                    host: {
                        class: 'uxd-sidenav-body'
                    }
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }]; }, propDecorators: { _panels: [{
                type: ContentChildren,
                args: [SidenavBodyPanelComponent]
            }], hostEl: [{
                type: ViewChild,
                args: ['host']
            }] } });

/**
 * SidenavComponent
 * Parent component that controls which tab and panel are active.
 * Wraps the SidenavLinkComponent, SidenavTabComponent, SidenavBodyPanelComponent, and SidenavBodyComponent.
 * https://www.w3.org/TR/2019/NOTE-wai-aria-practices-1.1-20190207/examples/tabs/tabs-1/tabs.html
 */
class SidenavComponent {
    constructor(_renderer, _changeDetectorRef, _ngZone) {
        this._renderer = _renderer;
        this._changeDetectorRef = _changeDetectorRef;
        this._ngZone = _ngZone;
        this._activeLinkChanges = defer(() => {
            if (this._tabs) {
                return merge(...this._tabs.map((item) => item.change));
            }
            return this._ngZone.onStable.asObservable().pipe(take(1), switchMap(() => this._activeLinkChanges));
        });
        this.selected = -1;
    }
    ngAfterViewInit() {
        if (this._tabs) {
            this._keyboardManager = new RovingTabindex(this._tabs);
            this._keyboardManager.change.subscribe((tab) => {
                this._activateTab(tab);
            });
        }
        const items = this._tabs.toArray();
        if (this.selected > -1) {
            items[this.selected].isActive = true;
            if (this._tabBody.hasPanels) {
                this._tabBody.setSelected(this.selected);
            }
        }
        this._activeLinkChanges.subscribe((tab) => {
            this._activateTab(tab);
        });
        if (this._tabBody) {
            this._tabBody.tabLabelIds = this._getTabIds();
            const panelIds = this._getPanelIds();
            items.forEach((item, idx) => {
                item._panelId = panelIds[idx];
            });
        }
    }
    _handleKeyDown(event) {
        this._keyboardManager.handleKeyboard(event);
    }
    _getPanelIds() {
        const panels = this._tabBody && this._tabBody.panels;
        if (panels && panels.length) {
            return panels.map((panel) => panel.id);
        }
        return [];
    }
    _getTabIds() {
        const links = this._tabs && this._tabs.toArray();
        if (links && links.length) {
            return links.map((link) => link.id);
        }
        return [];
    }
    _activateTab(tab) {
        this._tabs.forEach((item, idx) => {
            if (item !== tab) {
                item.isActive = false;
            }
            else {
                this.selected = idx;
            }
        });
        tab.isActive = true;
        if (this._tabBody.hasPanels) {
            this._tabBody.setSelected(this.selected);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavComponent, deps: [{ token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SidenavComponent, selector: "uxd-sidenav", inputs: { ariaLabel: "ariaLabel", selected: "selected" }, queries: [{ propertyName: "_tabBody", first: true, predicate: SidenavBodyComponent, descendants: true }, { propertyName: "_tabs", predicate: SidenavTabComponent }], ngImport: i0, template: "<div class=\"uxd-sidenav-wrapper\">\n  <ng-content select=\"uxd-sidenav-nav\"></ng-content>\n  <div role=\"tablist\"\n    (keydown)=\"_handleKeyDown($event)\"\n    [attr.aria-label]=\"ariaLabel\" class=\"uxd-sidenav-tabs\">\n    <ng-content select=\"[uxd-sidenav-tab]\"></ng-content>\n  </div>\n  <ng-content select=\"uxd-sidenav-body\"></ng-content>\n</div>" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavComponent, decorators: [{
            type: Component,
            args: [{ selector: 'uxd-sidenav', template: "<div class=\"uxd-sidenav-wrapper\">\n  <ng-content select=\"uxd-sidenav-nav\"></ng-content>\n  <div role=\"tablist\"\n    (keydown)=\"_handleKeyDown($event)\"\n    [attr.aria-label]=\"ariaLabel\" class=\"uxd-sidenav-tabs\">\n    <ng-content select=\"[uxd-sidenav-tab]\"></ng-content>\n  </div>\n  <ng-content select=\"uxd-sidenav-body\"></ng-content>\n</div>" }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }, { type: i0.NgZone }]; }, propDecorators: { ariaLabel: [{
                type: Input
            }], _tabBody: [{
                type: ContentChild,
                args: [SidenavBodyComponent]
            }], _tabs: [{
                type: ContentChildren,
                args: [SidenavTabComponent]
            }], selected: [{
                type: Input
            }] } });

class SidenavModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SidenavModule, declarations: [SidenavComponent, SidenavNavComponent, SidenavTabComponent, SidenavBodyComponent, SidenavBodyPanelComponent, TabPanelContentDirective, SidenavLinkComponent], imports: [CommonModule, PortalModule], exports: [SidenavComponent, SidenavNavComponent, SidenavTabComponent, SidenavBodyComponent, SidenavBodyPanelComponent, TabPanelContentDirective, SidenavLinkComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavModule, imports: [CommonModule, PortalModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidenavModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, PortalModule],
                    declarations: [SidenavComponent, SidenavNavComponent, SidenavTabComponent, SidenavBodyComponent, SidenavBodyPanelComponent, TabPanelContentDirective, SidenavLinkComponent],
                    exports: [SidenavComponent, SidenavNavComponent, SidenavTabComponent, SidenavBodyComponent, SidenavBodyPanelComponent, TabPanelContentDirective, SidenavLinkComponent]
                }]
        }] });

class SidebarIconsList {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidebarIconsList, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SidebarIconsList, selector: "[sidebar-icons-list]", ngImport: i0, template: `<ng-content></ng-content>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidebarIconsList, decorators: [{
            type: Component,
            args: [{
                    selector: '[sidebar-icons-list]',
                    template: `<ng-content></ng-content>`
                }]
        }] });
class SidebarPopoutNav {
    constructor() {
        this.mobileToggle = false;
    }
    toggleMobileNav() {
        this.mobileToggle = !this.mobileToggle;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidebarPopoutNav, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: SidebarPopoutNav, selector: "sidebar-popout-nav", ngImport: i0, template: "<div class=\"ant-sidebar-nav\" [ngClass]=\"{'open' : mobileToggle}\">\n  <div class=\"ant-sidebar-compose\">\n    <button type=\"button\" class=\"btn btn-secondary nav-toggle hidden\"\n      (click)=\"toggleMobileNav()\">\n      <span class=\"motif-icon motif-arrows-long-right\"></span>\n      <span class=\"sr-only\" [innerHTML]=\"'Toggle Nav'\"></span>\n    </button>\n  </div>\n  <ng-content select=\"sidebar-icons-list, [sidebar-icons-list]\"></ng-content>\n</div>", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidebarPopoutNav, decorators: [{
            type: Component,
            args: [{ selector: 'sidebar-popout-nav', template: "<div class=\"ant-sidebar-nav\" [ngClass]=\"{'open' : mobileToggle}\">\n  <div class=\"ant-sidebar-compose\">\n    <button type=\"button\" class=\"btn btn-secondary nav-toggle hidden\"\n      (click)=\"toggleMobileNav()\">\n      <span class=\"motif-icon motif-arrows-long-right\"></span>\n      <span class=\"sr-only\" [innerHTML]=\"'Toggle Nav'\"></span>\n    </button>\n  </div>\n  <ng-content select=\"sidebar-icons-list, [sidebar-icons-list]\"></ng-content>\n</div>" }]
        }] });

class SidebarPopoutNavModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidebarPopoutNavModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: SidebarPopoutNavModule, declarations: [SidebarIconsList, SidebarPopoutNav], imports: [CommonModule], exports: [SidebarIconsList, SidebarPopoutNav] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidebarPopoutNavModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: SidebarPopoutNavModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [SidebarIconsList, SidebarPopoutNav],
                    exports: [SidebarIconsList, SidebarPopoutNav]
                }]
        }] });

class AntPaginationModel {
}
class AntPaginationAriaLabels {
}
class PaginationComponent {
    constructor() {
        this.ariaLabels = {
            navLabel: 'Pagination Navigation',
            nextPageLabel: 'Next Page',
            prevPageLabel: 'Previous Page'
        };
        this._viewMax = 5;
        this._startPage = 0;
        this._endPage = 5;
        this.showNextBtn = true;
        this.showNextEllipsis = false;
        this.showPrevEllipsis = false;
        this.pages = [];
        this.updateActivePage = new EventEmitter();
    }
    ngOnInit() {
        this.totalPages = this.model.totalPages;
        this.linkStart = this._startPage;
        this.linkEnd = (this.totalPages > this._endPage) ? this._endPage : this.totalPages;
        for (let i = 0; i < this.totalPages; i++) {
            this.pages.push({ id: i + 1 });
        }
        this.slicer(this.model.activePage, false);
        this.updateNavButtons();
    }
    thisPage(page) {
        this.slicer(page.id, true);
    }
    prevPage() {
        if (this.model.activePage > 1) {
            this.slicer(this.model.activePage - 1, true);
            // 508, reset focus to first item when prev arrow is hidden
            if (this.model.activePage === 1) {
                const btns = document.querySelectorAll('.page-num');
                const first = btns[0];
                first.focus();
            }
        }
    }
    nextPage() {
        if (this.model.activePage < this.pages.length) {
            this.slicer(this.model.activePage + 1, true);
            // 508, reset focus to last item when next arrow is hidden
            if (this.model.activePage === this.pages.length) {
                const btns = document.querySelectorAll('.page-num');
                const last = btns[btns.length - 1];
                last.focus();
            }
        }
    }
    getNextEllipsisStatus(page) {
        return this._endPage < this.pages.length;
    }
    getPrevEllipsisStatus(page) {
        return page > 3 && this.model.totalPages > 5;
    }
    getNextBtnStatus(page) {
        return page === this.pages.length ? false : true;
    }
    updateNavButtons() {
        this.showNextBtn = this.getNextBtnStatus(this.model.activePage);
        this.showNextEllipsis = this.getNextEllipsisStatus(this.model.activePage);
        this.showPrevEllipsis = this.getPrevEllipsisStatus(this.model.activePage);
    }
    slicer(next, emit) {
        let half = Math.ceil(this._viewMax / 2);
        let pen = this.pages.length - 1;
        // Keeps active page centered when half point is reached
        if (next >= half && next < pen) {
            this.linkStart = next - half;
            this.linkEnd = next + (half - 1);
        }
        // Moves active page off center if penultimate page is selected.
        if (this.pages.length > this._viewMax) {
            // last
            if (this.pages.length === next) {
                this.linkStart = next - this._viewMax;
                this.linkEnd = this.pages.length;
            }
            else if ((this.pages.length - next) <= 1 && (next - this.model.activePage) > 1) {
                // second to last
                this.linkStart = this.linkStart + 1;
                this.linkEnd = this.pages.length;
            }
            // second (to first)
            if (this.model.activePage - next > 1 && next < half) {
                this.linkStart = 0;
                this.linkEnd = this._viewMax;
            }
        }
        this.model.activePage = next;
        this._startPage = this.linkStart;
        this._endPage = this.linkEnd;
        if (emit) {
            this.updateActivePage.emit(this.model);
        }
        this.updateNavButtons();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaginationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: PaginationComponent, selector: "ant-pagination", inputs: { model: "model", ariaLabels: "ariaLabels" }, outputs: { updateActivePage: "updateActivePage" }, host: { classAttribute: "ant-pagination" }, ngImport: i0, template: "<nav class=\"ant-pagination\" id=\"er-pagination-testing\" role=\"navigation\" [attr.aria-label]=\"ariaLabels.navLabel\">\n\n  <a\n    href=\"javascript:void(0)\"\n    role=\"button\"\n    class=\"page-icon rotate page-item\"\n    (click)=\"prevPage()\"\n    [ngStyle]=\"{'visibility' : model.activePage <= 1 ? 'hidden' : 'visible'}\"\n    [attr.aria-hidden]=\"model.activePage <= 1 ? true : null\"\n    [attr.aria-label]=\"ariaLabels.prevPageLabel\"\n  ><span></span></a>\n\n  <a\n    href=\"javascript:void(0)\"\n    role=\"button\"\n    *ngIf=\"showPrevEllipsis\"\n    class=\"page-num page-item\"\n    (click)=\"thisPage(pages[0])\"\n  >\n    <span><span>{{pages[0].id}}</span></span>\n  </a>\n\n  <span class=\"ellipsis page-item\" *ngIf=\"showPrevEllipsis\"><span>...</span></span>\n\n  <a\n    href=\"javascript:void(0)\"\n    role=\"button\"\n    class=\"page-num page-item\"\n    [ngClass]=\"{'active' : page.id === model.activePage}\"\n    *ngFor=\"let page of pages | slice:linkStart:linkEnd\"\n    (click)=\"thisPage(page)\"\n    [attr.aria-current]=\"page.id === model.activePage ? true : null\"\n  >\n    <span><span>{{page.id}}</span></span>\n  </a>\n\n  <span class=\"ellipsis page-item\" *ngIf=\"showNextEllipsis\"><span>...</span></span>\n\n  <a\n    href=\"javascript:void(0)\"\n    role=\"button\"\n    *ngIf=\"showNextEllipsis\"\n    class=\"page-num page-item\"\n    (click)=\"thisPage(pages[pages.length - 1])\"\n  >\n    <span><span>{{pages[pages.length - 1].id}}</span></span>\n  </a>\n\n  <a\n    href=\"javascript:void(0)\"\n    role=\"button\"\n    class=\"page-icon page-item\"\n    (click)=\"nextPage()\"\n    [ngStyle]=\"{'visibility' : showNextBtn && pages.length > 0 ? 'visible' : 'hidden'}\"\n    [attr.aria-hidden]=\"showNextBtn && pages.length > 0 ? null : true\"\n    [attr.aria-label]=\"ariaLabels.nextPageLabel\"\n  >\n    <span></span>\n  </a>\n\n</nav>", dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "pipe", type: i1.SlicePipe, name: "slice" }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaginationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ant-pagination', encapsulation: ViewEncapsulation.None, host: {
                        class: 'ant-pagination'
                    }, template: "<nav class=\"ant-pagination\" id=\"er-pagination-testing\" role=\"navigation\" [attr.aria-label]=\"ariaLabels.navLabel\">\n\n  <a\n    href=\"javascript:void(0)\"\n    role=\"button\"\n    class=\"page-icon rotate page-item\"\n    (click)=\"prevPage()\"\n    [ngStyle]=\"{'visibility' : model.activePage <= 1 ? 'hidden' : 'visible'}\"\n    [attr.aria-hidden]=\"model.activePage <= 1 ? true : null\"\n    [attr.aria-label]=\"ariaLabels.prevPageLabel\"\n  ><span></span></a>\n\n  <a\n    href=\"javascript:void(0)\"\n    role=\"button\"\n    *ngIf=\"showPrevEllipsis\"\n    class=\"page-num page-item\"\n    (click)=\"thisPage(pages[0])\"\n  >\n    <span><span>{{pages[0].id}}</span></span>\n  </a>\n\n  <span class=\"ellipsis page-item\" *ngIf=\"showPrevEllipsis\"><span>...</span></span>\n\n  <a\n    href=\"javascript:void(0)\"\n    role=\"button\"\n    class=\"page-num page-item\"\n    [ngClass]=\"{'active' : page.id === model.activePage}\"\n    *ngFor=\"let page of pages | slice:linkStart:linkEnd\"\n    (click)=\"thisPage(page)\"\n    [attr.aria-current]=\"page.id === model.activePage ? true : null\"\n  >\n    <span><span>{{page.id}}</span></span>\n  </a>\n\n  <span class=\"ellipsis page-item\" *ngIf=\"showNextEllipsis\"><span>...</span></span>\n\n  <a\n    href=\"javascript:void(0)\"\n    role=\"button\"\n    *ngIf=\"showNextEllipsis\"\n    class=\"page-num page-item\"\n    (click)=\"thisPage(pages[pages.length - 1])\"\n  >\n    <span><span>{{pages[pages.length - 1].id}}</span></span>\n  </a>\n\n  <a\n    href=\"javascript:void(0)\"\n    role=\"button\"\n    class=\"page-icon page-item\"\n    (click)=\"nextPage()\"\n    [ngStyle]=\"{'visibility' : showNextBtn && pages.length > 0 ? 'visible' : 'hidden'}\"\n    [attr.aria-hidden]=\"showNextBtn && pages.length > 0 ? null : true\"\n    [attr.aria-label]=\"ariaLabels.nextPageLabel\"\n  >\n    <span></span>\n  </a>\n\n</nav>" }]
        }], ctorParameters: function () { return []; }, propDecorators: { model: [{
                type: Input
            }], ariaLabels: [{
                type: Input
            }], updateActivePage: [{
                type: Output
            }] } });

class PaginationModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaginationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: PaginationModule, declarations: [PaginationComponent], imports: [CommonModule], exports: [PaginationComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaginationModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaginationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [PaginationComponent],
                    exports: [PaginationComponent]
                }]
        }] });

class NavigationModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NavigationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: NavigationModule, declarations: [SubNavComponent], imports: [CommonModule, SidebarRailModule, SidenavModule, SidebarPopoutNavModule, PaginationModule], exports: [SubNavComponent, SidebarRailModule, SidenavModule, SidebarPopoutNavModule, PaginationModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NavigationModule, imports: [CommonModule, SidebarRailModule, SidenavModule, SidebarPopoutNavModule, PaginationModule, SidebarRailModule, SidenavModule, SidebarPopoutNavModule, PaginationModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: NavigationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, SidebarRailModule, SidenavModule, SidebarPopoutNavModule, PaginationModule],
                    declarations: [SubNavComponent],
                    exports: [SubNavComponent, SidebarRailModule, SidenavModule, SidebarPopoutNavModule, PaginationModule]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AntPaginationAriaLabels, AntPaginationModel, NavigationModule, PaginationComponent, PaginationModule, SideRailHeaderDirective, SideRailLinkComponent, SideRailNavComponent, SideRailNavEvent, SidebarIconsList, SidebarPopoutNav, SidebarPopoutNavModule, SidebarRailComponent, SidebarRailModule, SidenavBodyComponent, SidenavBodyPanelComponent, SidenavComponent, SidenavLinkComponent, SidenavModule, SidenavNavComponent, SidenavTabComponent, SubNavComponent, TabPanelContentDirective };
